<template>
  <div class="staff-organization-detail">
    <LoadSpinner v-if="loadingOrganization" style="padding: 2rem 0" />

    <div v-if="organization" class="container">
      <div class="sidebar">
        <OrganizationCard :organization="organization" :report="report" />
        <br />
      </div>

      <div class="organization-content">

          <TabContainer
            @changed-tab="onChangedTab"
          >
            <Tab
              name="Overview"
              :selected="selectedTabName == 'Overview'"
            >
              <div class="OrganizationDetail__Config">
                <h5 class="text-blue text-upper text-small OrganizationDetail__ConfigHeading">Basic Info</h5>

                <div class="OrganizationDetail__ConfigSection">
                  <h3 class="OrganizationDetail__ConfigSection__Heading">
                    Identity
                  </h3>
                  <div class="OrganizationDetail__ConfigEntry">
                    <span class="OrganizationDetail__ConfigKey">Name:</span>
                    <span class="OrganizationDetail__ConfigValue">{{organization.name}}</span>
                  </div>
                  <div class="OrganizationDetail__ConfigEntry">
                    <span class="OrganizationDetail__ConfigKey">Subdomain:</span>
                    <span class="OrganizationDetail__ConfigValue">{{organization.subdomain}}</span>
                  </div>
                  <div class="OrganizationDetail__ConfigEntry">
                    <span class="OrganizationDetail__ConfigKey">Logo:</span>
                    <div class="OrganziationDetail__ConfigImage">
                      <img class="logo" :src="organization.logo" />
                    </div>
                  </div>
                  <div class="OrganizationDetail__ConfigEntry">
                    <span class="OrganizationDetail__ConfigKey">Support Email:</span>
                    <span class="OrganizationDetail__ConfigValue">{{organization.supportEmail}}</span>
                  </div>
                  <div class="OrganizationDetail__ConfigEntry">
                    <span class="OrganizationDetail__ConfigKey">Payroll Email:</span>
                    <span class="OrganizationDetail__ConfigValue">{{organization.payrollEmail}}</span>
                  </div>
                </div>

                <div class="OrganizationDetail__ConfigSection">
                  <h3 class="OrganizationDetail__ConfigSection__Heading">
                    Messaging
                  </h3>
                  <div class="OrganizationDetail__ConfigEntry">
                    <span class="OrganizationDetail__ConfigKey">Organization Message Title:</span>
                    <span class="OrganizationDetail__ConfigValue">{{organization.organizationMessage && organization.organizationMessage.title}}</span>
                  </div>
                  <div class="OrganizationDetail__ConfigEntry">
                    <span class="OrganizationDetail__ConfigKey">Organization Message Content:</span>
                    <span class="OrganizationDetail__ConfigValue">{{organization.organizationMessage && organization.organizationMessage.content}}</span>
                  </div>
                </div>

                <div class="OrganizationDetail__ConfigSection">
                  <h3 class="OrganizationDetail__ConfigSection__Heading">
                    Lifecycle
                  </h3>
                  <div class="OrganizationDetail__FeatureFlag">
                    <span class="OrganizationDetail__FeatureFlag__Label">
                      Is Ready For Onboarding
                    </span>
                    <FeatureFlag :enabled="organization.isReadyForOnboarding" />
                  </div>

                  <div class="OrganizationDetail__FeatureFlag">
                    <span class="OrganizationDetail__FeatureFlag__Label">
                      Is Referral Organization
                    </span>
                    <FeatureFlag :enabled="organization.isReferralOrganization" />
                  </div>
                </div>

                <div class="OrganizationDetail__ConfigSection">
                  <h3 class="OrganizationDetail__ConfigSection__Heading">
                    Financial Setup
                  </h3>
                  <div class="OrganizationDetail__ConfigEntry">
                    <span class="OrganizationDetail__ConfigKey">Fund Balance Alert Amount:</span>
                    <span class="OrganizationDetail__ConfigValue">TBD</span>
                  </div>
                  <div class="OrganizationDetail__ConfigEntry">
                    <span class="OrganizationDetail__ConfigKey">Verification Method:</span>
                    <span class="OrganizationDetail__ConfigValue">{{organization.verificationMethod}}</span>
                  </div>
                </div>

                <div class="OrganizationDetail__ConfigSection">
                  <h3 class="OrganizationDetail__ConfigSection__Heading">
                    Feature Flags
                  </h3>

                  <div class="OrganizationDetail__FeatureFlag">
                    <span class="OrganizationDetail__FeatureFlag__Label">
                      Donations Enabled
                    </span>
                    <FeatureFlag :enabled="organization.donationsEnabled" />
                  </div>

                  <div class="OrganizationDetail__FeatureFlag">
                    <span class="OrganizationDetail__FeatureFlag__Label">
                      Expense Documentation Enabled
                    </span>
                    <FeatureFlag :enabled="organization.expenseDocumentationEnabled" />
                  </div>

                  <div class="OrganizationDetail__FeatureFlag">
                    <span class="OrganizationDetail__FeatureFlag__Label">
                      Event Documentation Enabled
                    </span>
                    <FeatureFlag :enabled="organization.eventDocumentationEnabled" />
                  </div>

                  <div class="OrganizationDetail__FeatureFlag">
                    <span class="OrganizationDetail__FeatureFlag__Label">
                      Grant Request Amount Enabled
                    </span>
                    <FeatureFlag :enabled="organization.grantRequestAmountEnabled" />
                  </div>

                  <div class="OrganizationDetail__FeatureFlag">
                    <span class="OrganizationDetail__FeatureFlag__Label">
                      Enable Language Preference
                    </span>
                    <FeatureFlag :enabled="organization.enableLanguagePref" />
                  </div>

                  <div class="OrganizationDetail__FeatureFlag">
                    <span class="OrganizationDetail__FeatureFlag__Label">
                      Is Financial Health Survey Enabled
                    </span>
                    <FeatureFlag :enabled="organization.isFinancialHealthSurveyEnabled" />
                  </div>

                  <div class="OrganizationDetail__FeatureFlag">
                    <span class="OrganizationDetail__FeatureFlag__Label">
                      Allows Payroll Requests
                    </span>
                    <FeatureFlag :enabled="organization.allowsPayrollRequests" />
                  </div>

                  <div class="OrganizationDetail__FeatureFlag">
                    <span class="OrganizationDetail__FeatureFlag__Label">
                      Spring Four Enabled
                    </span>
                    <FeatureFlag :enabled="organization.springFourEnabled" />
                  </div>
                </div>

                <div class="OrganizationDetail__ConfigSection">
                  <h3 class="OrganizationDetail__ConfigSection__Heading">
                    Custom Variables
                  </h3>

                  <template v-for="(value, variableSymbol) in organization.customVariables">
                    <div class="OrganizationDetail__ConfigEntry">
                      <span class="OrganizationDetail__ConfigKey">{{variableSymbol}}</span>
                      <span class="OrganizationDetail__ConfigValue">{{value}}</span>
                    </div>
                    <p class="OrganizationDetail__ConfigEntrySubscript"><small>{{customVariableName(variableSymbol)}}</small></p>
                  </template>
                </div>
              </div>
            </Tab>

            <Tab
              name="Events"
              :selected="selectedTabName == 'Events'"
            >
              <h5 class="text-blue text-upper text-small OrganizationDetail__ConfigHeading">Events</h5>

              <LoadSpinner v-if="loadingEvents" style="padding: 2rem 0" />

              <div class="OrganizationDetail__ConfigSection" v-else>
                <template v-if="eventCategories && eventCategories.length > 0">
                  <div v-for="eventCategory in eventCategories">
                    <EventCategoryCard :eventCategory="eventCategory" />
                  </div>
                </template>
                <template v-else>
                <p>No Event Categories associated with this organization.</p>
                </template>
              </div>
            </Tab>

            <Tab
              name="Expenses"
              :selected="selectedTabName == 'Expenses'"
            >
              <h5 class="text-blue text-upper text-small OrganizationDetail__ConfigHeading">Expenses</h5>

              <LoadSpinner v-if="loadingExpenses" style="padding: 2rem 0" />

              <div class="OrganizationDetail__ConfigSection" v-else>
                <template v-if="expenseCategories && expenseCategories.length > 0">
                  <div v-for="expenseCategory in expenseCategories">
                    <ExpenseCategoryCard :expenseCategory="expenseCategory" />
                  </div>
                </template>
                <template v-else>
                  <p>No Expense Categories associated with this organization.</p>
                </template>
              </div>
            </Tab>

            <Tab
              name="Surveys"
              :selected="selectedTabName == 'Surveys'"
            >
              <h5 class="text-blue text-upper text-small OrganizationDetail__ConfigHeading">Surveys</h5>

              <LoadSpinner v-if="loadingSurveys" style="padding: 2rem 0" />

              <div class="OrganizationDetail__ConfigSection" v-else>
                <template v-if="surveys && surveys.length > 0">
                  <div v-for="survey in surveys">
                    <SurveyCard :survey="survey" />
                  </div>
                </template>
                <template v-else>
                  <p>No surveys configured</p>
                </template>
              </div>
            </Tab>

            <Tab
              name="Copy"
              :selected="selectedTabName == 'Copy'"
            >
              <h5 class="text-blue text-upper text-small OrganizationDetail__ConfigHeading">Copy</h5>

              <LoadSpinner v-if="loadingCopy" style="padding: 2rem 0" />

              <div class="OrganizationDetail__ConfigSection" v-else>
                <template v-if="organizationCopy">
                  <div v-for="(content, label) in organizationCopy">
                    <OrganizationCopyCard :label="label" :content="content" />
                  </div>
                </template>
                <template v-else>
                  <p>No Organization Copy associated with this organization.</p>
                </template>
              </div>
            </Tab>

          </TabContainer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import { DocumentDownload, LoadSpinner } from '@/components'
import FeatureFlag from '@/components/FeatureFlag'
import { formatNumberAsUSD } from '@/services/utils'

import CollectionManager from '@/services/collectionManager'

import EventCategory from '@/services/eventCategories'
import ExpenseCategory from '@/services/expenseCategories'
import Survey from '@/services/surveys'
import Report from '@/services/reporting'

import organization from '@/services/organizations'
import Staff from '@/services/staff'
import OrganizationCard from '@/views/common/OrganizationCard'
import EventCategoryCard from '@/views/common/EventCategoryCard'
import ExpenseCategoryCard from '@/views/common/ExpenseCategoryCard'
import SurveyCard from '@/views/common/SurveyCard'
import OrganizationCopyCard from '@/views/common/OrganizationCopyCard'

import Tab from '@/components/Tab'
import TabContainer from '@/components/TabContainer'

import { translate } from '@/services/translations'
const $gettext = translate.gettext.bind(translate)

export default {
  name: 'organizationDetail',
  components: {
    LoadSpinner,
    OrganizationCard,
    EventCategoryCard,
    ExpenseCategoryCard,
    SurveyCard,
    OrganizationCopyCard,
    FeatureFlag,
    Tab,
    TabContainer
  },
  data() {
    return {
      selectedEmailContent: '',

      organization: null,

      eventCategories: null,
      expenseCategories: null,
      surveys: null,
      organizationCopy: null,
      report: null,

      selectedTabName: "Overview",

      loadingOrganization: false,
      loadingEvents: false,
      loadingExpenses: false,
      loadingSurveys: false,
      loadingReport: false,
      loadingCopy: false,
    }
  },
  computed: {
  ...mapState(['user', 'organizationDetails']),
  },
  created() {
    this.refresh()
  },
  methods: {
    refresh() {
      this.loadingOrganization = true
      const organizationPromise = organization.api
        .retrieve(this.$route.params.id)
        .then(organization => {
          this.organization = organization
        }).finally(() => {
          this.loadingOrganization = false
        })
    },
    customVariableName(variableSymbol) {
      const symbolToName = {
        "cv1": "organization variable",
        "cv2": "colleagues variable",
        "cv3": "Relationship to organization variable",
        "cv7": "Action phrase describing how the end user is related to the sponsoring organization",
        "cv4": "Charitable entity name, with explanation if needed",
        "cv5": "Charitable entity name",
        "cv6": "Charitable entity taxID",
        "cv8": "Lookback period copy",
      }
      const foundName = symbolToName[variableSymbol]

      return foundName ? `(${foundName})` : `(custom variable ${variableSymbol})`
    },
    onChangedTab(newTabName) {
      this.selectedTabName = newTabName
    }
  },
  watch: {
    organization() {
      this.loadingEvents = true
      EventCategory.api.list({
        filters: { event_category_organization: this.organization.id },
      }).then(eventCategories => {
        this.eventCategories = eventCategories.results
      }).finally(() => {
        this.loadingEvents = false
      })

      this.loadingExpenses = true
      ExpenseCategory.api.list({
        filters: {
          expense_category_organization: this.organization.id
        },
      }).then(expenseCategories => {
        this.expenseCategories = expenseCategories.results
      }).finally(() => {
        this.loadingExpenses = false
      })

      this.loadingSurveys = true

      const surveyIds = this.organization.organizationSurveys.map(s => s.surveyId)
      const surveyFetches = surveyIds.map(sid => Survey.api.retrieve(sid))

      Promise.allSettled(surveyFetches).then((results) => {
        const onlySuccess = (acc, req) => req.status == "fulfilled"
          ? acc.concat(req.value)
          : acc

        this.surveys = results.reduce(onlySuccess, [])
        this.loadingSurveys = false
      })

      this.loadingReport = true
      Report.api
      .retrieve(this.organization.id)
      .then((report) => {
        this.report = report
      })
      .finally(() => {
        this.loadingReport = false
      })

      console.dir(this.organization)

      this.organizationCopy = {
        "Homepage Copy": this.organization.homepageCopy,
        "Contribution Copy": this.organization.contributionCopy,
        "Unique ID Help Copy": this.organization.uniqueIdHelpCopy,
        "Additional Resources Copy": this.organization.additionalResourcesCopy,
        "Hardship Definition Copy": this.organization.hardshipDefinitionCopy,
        "Eligibility List Copy": this.organization.eligibilityListCopy,
        "Qualification Copy": this.organization.qualificationCopy,
        "Fraud Warning Copy": this.organization.fraudWarningCopy,
        "Documentation Copy": this.organization.documentationCopy
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../styles/mixins/containers';
@import '@/styles/variables';

.staff-organization-detail {
  @include screen-container;
}

.container {
  @include flex-container;
}

.organization-content {
  flex: 12;
  width: 60%;
  margin-left: 2em;

  &__section {
    padding: 1rem;

    &--bordered {
      padding-top: 2rem;
      border-top: 1px solid $soft-gray;
    }

    &-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
}

.sidebar {
  padding: 0 1rem 0 0;
}

.OrganizationDetail__Config {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1em auto auto;
  gap: 2em;
  margin-bottom: 4em;
}

.OrganizationDetail__ConfigHeading {
  grid-column: 1 / span 2;
}

.OrganizationDetail__ConfigSection {
  margin-bottom: 2em;
  border: 1px solid #eeeeee;
  padding: 1.5rem;
  min-width: 250px;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  -webkit-box-shadow: 0 0 0 1px rgba(63, 63, 68, 0.05), 0 1px 3px 0 rgba(63, 63, 68, 0.15);
  box-shadow: 0 0 0 1px rgba(63, 63, 68, 0.05), 0 1px 3px 0 rgba(63, 63, 68, 0.15);
}

.OrganizationDetail__ConfigSection__Heading {
  font-size: 24px;
  line-height: 2em;
  margin-bottom: 1em;
}

.OrganizationDetail__ConfigEntrySubscript {
  font-style: italic;
  text-style: small;
  margin-bottom: 1.5em;
}

.OrganizationDetail__ConfigEntry {
  display: flex;
}

.OrganizationDetail__ConfigKey {
  font-weight: bold;
  margin-right: 0.75em;
}

.OrganizationDetail__ConfigValue {
  font-weight: light;
}

.OrganziationDetail__ConfigImage {
  display: flex;
  align-items: center;
}

.OrganizationDetail__ConfigImage > img {
  height: 12rem;
  width: 100%;
}

.OrganizationDetail__FeatureFlag {
  display: flex;
  align-content: center;
  margin-top: 28px;
  margin-bottom: 28px;
}

.OrganizationDetail__FeatureFlag__Label {
  margin-right: 12px;
  font-weight: bold;
}
</style>
